import React, { useState, useEffect, useRef, useCallback } from "react";
import { api, fileUrl } from "../lib/apiClient";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Loader2, X, Image as ImageIcon, Sparkles, Home, Building2 } from "lucide-react";
import { toast } from "sonner";

const STYLES = ["Modern", "Contemporary", "Scandinavian", "Minimalist", "Industrial", "Farmhouse", "Mid-Century"];

export default function LivePreview({ blueprint, name, roofStyle, floors, lot, sourcePhoto, terrain, onClose }) {
  const [view, setView] = useState("interior");
  const [style, setStyle] = useState("Modern");
  const [auto, setAuto] = useState(false);
  const [lock, setLock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const debRef = useRef(null);

  const stop = () => {
    if (pollRef.current) clearTimeout(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    pollRef.current = null; timerRef.current = null;
  };

  const render = useCallback(async (v) => {
    const useView = v || view;
    if (!blueprint.rooms?.length) { toast.error("Draw or generate a plan first"); return; }
    stop(); setLoading(true); setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    try {
      const allFloors = (floors && floors.length) ? floors : [{ name: "Ground Floor", rooms: blueprint.rooms }];
      const res = await api.post("/ai/live-preview", {
        name: name || "House", style, view: useView, roof_style: roofStyle || "", rooms: blueprint.rooms,
        stories: allFloors.length, floors: allFloors,
        footprint_w: lot?.w ? Math.round(lot.w) : 0, footprint_d: lot?.h ? Math.round(lot.h) : 0,
        ref_path: (lock && path) ? path : (useView === "exterior" ? (sourcePhoto || null) : null),
        terrain: terrain || "",
      });
      const tid = res.data.task_id;
      const poll = async () => {
        try {
          const st = await api.get(`/ai/render-status/${tid}`);
          if (st.data.status === "completed") { stop(); setPath(st.data.render_path); setLoading(false); }
          else if (st.data.status === "failed") { stop(); setLoading(false); toast.error(st.data.error || "Render failed"); }
          else pollRef.current = setTimeout(poll, 3000);
        } catch { pollRef.current = setTimeout(poll, 4000); }
      };
      pollRef.current = setTimeout(poll, 3000);
    } catch (e) {
      stop(); setLoading(false);
      toast.error(e.response?.data?.detail || "Render failed");
    }
  }, [blueprint.rooms, name, style, view, roofStyle, lock, path, floors, lot, sourcePhoto, terrain]);

  const renderView = (v) => { setView(v); render(v); };

  // Auto re-render (debounced) when the plan or options change
  const sig = JSON.stringify((blueprint.rooms || []).map((r) => [r.name, r.x, r.y, r.w, r.h]));
  useEffect(() => {
    if (!auto) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { render(); }, 6000);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [sig, auto, view, style]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { stop(); if (debRef.current) clearTimeout(debRef.current); }, []);

  return (
    <div className="w-[42%] min-w-[300px] shrink-0 border-l border-[#30363D] bg-[#0B1017] flex flex-col" data-testid="live-preview-pane">
      <div className="border-b border-[#30363D] p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-heading font-semibold text-[#F0F6FC]">
            <Sparkles className="w-4 h-4 text-[#38BDF8]" /> Live Render
            {auto && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#34D399]/15 text-[#34D399] font-mono-cad">AUTO</span>}
          </div>
          <button onClick={onClose} className="text-[#6E7681] hover:text-[#F0F6FC]" data-testid="live-preview-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger data-testid="live-style-select" className="bg-[#161B22] border-[#30363D] h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
              {STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-[#8B949E] cursor-pointer select-none" data-testid="live-auto-toggle">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="accent-[#34D399]" />
            Auto
          </label>
          <label className="flex items-center gap-1.5 text-xs text-[#8B949E] cursor-pointer select-none" data-testid="live-lock-toggle" title="Keep the same look between refreshes">
            <input type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)} className="accent-[#F59E0B]" disabled={!path} />
            Lock look
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button size="sm" onClick={() => renderView("interior")} disabled={loading} data-testid="live-render-interior-btn"
            className={`h-8 font-semibold ${view === "interior" ? "bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117]" : "bg-[#161B22] border border-[#30363D] text-[#F0F6FC] hover:border-[#38BDF8]"}`}>
            {loading && view === "interior" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Home className="w-3.5 h-3.5 mr-1.5" />} Interior
          </Button>
          <Button size="sm" onClick={() => renderView("exterior")} disabled={loading} data-testid="live-render-exterior-btn"
            className={`h-8 font-semibold ${view === "exterior" ? "bg-[#F59E0B] hover:bg-[#D97706] text-[#0D1117]" : "bg-[#161B22] border border-[#30363D] text-[#F0F6FC] hover:border-[#F59E0B]"}`}>
            {loading && view === "exterior" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5 mr-1.5" />} Exterior
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 flex items-center justify-center">
        {path ? (
          <img src={fileUrl(path)} alt="live render" className="w-full rounded-lg border border-[#30363D]" data-testid="live-preview-image" />
        ) : loading ? (
          <div className="text-center text-[#8B949E]">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#38BDF8]" />
            <div className="text-sm">Rendering your {view}…</div>
            <div className="text-xs font-mono-cad mt-1">{elapsed}s · usually 30–60s</div>
          </div>
        ) : (
          <div className="text-center text-[#6E7681] max-w-[220px]">
            <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <div className="text-sm">Tap <span className="text-[#38BDF8]">Interior</span> or <span className="text-[#F59E0B]">Exterior</span> to see a photoreal view of this floor plan, or turn on <span className="text-[#34D399]">Auto</span> to refresh as you edit.</div>
          </div>
        )}
      </div>
    </div>
  );
}
